import { useMemo, useRef, useState } from 'react'
import type { SubmitHandler } from 'react-hook-form'
import toast from 'react-hot-toast'
import { AiPromptModal } from '@/components/commons/ai-prompt-modal'
import { ButtonBrandLegacy } from '@/components/ui/button-brand-legacy'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { Container } from '@/components/ui/container'
import { Flexbox } from '@/components/ui/flexbox'
import { Settings01Icon, Stars01Icon } from '@/components/ui/icons'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Scrollbar } from '@/components/ui/scrollbar'
import { Separator } from '@/components/ui/separator'
import { Spinner } from '@/components/ui/spinner'
import { useAuthStore } from '@/features/auth/store/store'
import { OpsVettingHero } from '@/features/ops-vetting/components/ops-vetting-atoms'
import { useMerchCopyAi } from '@/features/ops-vetting/hooks/use-merch-copy-ai'
import { SERVICES_PROMPT } from '@/features/ops-vetting/prompts/service-prompt'
import { useSubmitService } from '@/features/service-vetting/query/use-submit-service'
import { errorHandler } from '@/helpers/utils'
import { useTranslation } from '@/translations/i18n'
import type {
	ServiceMerchandisingInputSchema,
	ServiceMerchandisingOutputSchema
} from '../form-schemas'
import type { RemappedService } from '../types'
import {
	remapMerchCopyAiSubmittedForm,
	remapMerchCopyHostEdits,
	remapMerchCopyHostEditsDiffs,
	remapMerchCopyInputForm
} from '../utils'
import { ServiceVettingMerchCopyDiffs } from './service-vetting-merch-copy-diffs'
import { ServiceVettingMerchInputForm } from './service-vetting-merch-input-form'
import { ServiceVettingMerchOutputForm } from './service-vetting-merch-output-form'
import { ServiceVettingMerchSubmitModal } from './service-vetting-merch-submit-modal'
export const ServiceVettingMerchForms = ({
	service
}: {
	service: RemappedService
}) => {
	//user roles
	const user = useAuthStore((state) => state.user)
	const isReadOnlyUser = user?.role === 'read_only'
	const isMerchandisingEditor = user?.role === 'merchandising_editor'
	const { t } = useTranslation()
	const {
		aiResponse,
		isLoading: isAiLoading,
		onMerchCopyAiCall,
		resetAiResponse
	} = useMerchCopyAi<ServiceMerchandisingOutputSchema>()
	const isApprovalRequired =
		service.status === 'archived_pending_host_approval' ||
		service.status === 'archived_update_required'
	const isServiceApproved =
		service.status === 'approved' ||
		service.status === 'publishing_enabled' ||
		service.status === 'pending_publishing' ||
		service.status === 'completed' ||
		service.status === 'pending_export' ||
		service.status === 'pending_upgrade_api' ||
		service.status === 'published_and_upgraded' ||
		service.status === 'pending_licensing'
	const isReviewHostEdits = service.is_host_modified
	const isFormReadOnly =
		isServiceApproved || isApprovalRequired || isReadOnlyUser
	//prompt controls
	const [currentPrompt, setCurrentPrompt] = useState<string>(SERVICES_PROMPT)
	//submission refs
	const finalSubmitRef = useRef<HTMLButtonElement>(null)
	const aiSubmissionRef = useRef<HTMLButtonElement>(null)
	//submit utils
	const [isSubmitMerchModalOpen, setIsSubmitMerchModalOpen] = useState(false)
	const [requireHostApproval, setRequireHostApproval] = useState(true)
	//submit service mutation
	const { mutate: submitMerchCopy, isPending: isSubmittingMerchCopy } =
		useSubmitService(
			{ serviceId: service.id },
			{
				onSettled: () =>
					setIsSubmitMerchModalOpen(!isSubmitMerchModalOpen),
				onError: (error) => errorHandler(error),
				onSuccess: () =>
					toast.success(t('vetting:merch.service.submit_success'))
			}
		)
	//trigger ai-improvement submission
	const handleAiSubmission: SubmitHandler<ServiceMerchandisingInputSchema> = (
		data
	) => {
		resetAiResponse()
		onMerchCopyAiCall(data, currentPrompt)
	}
	//final submission
	const handleFinalSubmission: SubmitHandler<
		ServiceMerchandisingOutputSchema
	> = (data) => {
		const payload = {
			...data,
			is_host_approval_needed: requireHostApproval,
			is_waitlisted: false
		}
		submitMerchCopy(payload)
	}

	// Memoize the input form default values
	const inputFormDefaultValues = useMemo(
		() => remapMerchCopyInputForm(service),
		[service]
	)
	// Memoize the output form default values
	const outputFormDefaultValues = useMemo(
		() =>
			aiResponse
				? (aiResponse as ServiceMerchandisingOutputSchema)
				: remapMerchCopyAiSubmittedForm(service),
		[service, aiResponse]
	)
	// Memoize the host edits default values
	const hostEditsDefaultValues = useMemo(
		() => remapMerchCopyHostEdits(service),
		[service]
	)
	// Memoize the diffs map
	const serviceDiffsMap = useMemo(
		() => remapMerchCopyHostEditsDiffs(service),
		[service]
	)

	return (
		<ScrollArea scrollbar={<Scrollbar />}>
			<Container
				className='grid grid-cols-[minmax(0,1fr)] grid-rows-[auto_minmax(0,1fr)]'
				fullWidth
			>
				<OpsVettingHero
					align='center'
					fullWidth
					justify='space-between'
				>
					<h3>{t('vetting:merch.service.title')}</h3>
					{!isFormReadOnly && (
						<Flexbox gap={2}>
							{!(isReviewHostEdits || isApprovalRequired) && (
								<>
									<AiPromptModal
										currentPrompt={currentPrompt}
										isLoading={isAiLoading}
										onPromptChange={(newPrompt) =>
											setCurrentPrompt(newPrompt)
										}
										promptType='services'
										trigger={
											<ButtonLegacy
												disabled={isAiLoading}
												type='button'
												variant='tertiary'
											>
												<Settings01Icon />
												{t(
													'vetting:merch.commons.customize_prompt'
												)}
											</ButtonLegacy>
										}
									/>
									<ButtonLegacy
										disabled={isAiLoading}
										onClick={() =>
											aiSubmissionRef.current?.click()
										}
										type='button'
										variant='secondary'
									>
										{isAiLoading ? (
											<Spinner size={16} />
										) : (
											<Stars01Icon />
										)}
										{t(
											'vetting:merch.commons.improve_with_ai'
										)}
									</ButtonLegacy>
								</>
							)}
							{!isMerchandisingEditor && (
								<>
									<ButtonBrandLegacy
										disabled={isAiLoading}
										onClick={() =>
											setIsSubmitMerchModalOpen(true)
										}
									>
										{t('vetting:merch.commons.submit')}
									</ButtonBrandLegacy>
									{isSubmitMerchModalOpen && (
										<ServiceVettingMerchSubmitModal
											isSubmitting={isSubmittingMerchCopy}
											onClose={() =>
												setIsSubmitMerchModalOpen(false)
											}
											setRequireHostApprovalCb={
												setRequireHostApproval
											}
											submissionRef={finalSubmitRef}
											type={
												isReviewHostEdits
													? 'host-edits'
													: 'ai'
											}
										/>
									)}
								</>
							)}
						</Flexbox>
					)}
				</OpsVettingHero>

				<div className='mt-6 mb-10 flex w-full overflow-hidden rounded-2xl border border-neutral-200'>
					{
						<>
							{!(isServiceApproved || isReviewHostEdits) && (
								<ServiceVettingMerchInputForm
									aiSubmissionRef={aiSubmissionRef}
									defaultValues={inputFormDefaultValues}
									handleAiSubmissionCb={handleAiSubmission}
									isReadOnly={isFormReadOnly}
								/>
							)}
							{(isServiceApproved || isReviewHostEdits) && (
								<ServiceVettingMerchOutputForm
									bgStyle={'light'}
									defaultValues={hostEditsDefaultValues}
									finalSubmitRef={finalSubmitRef}
									handleFinalSubmissionCb={
										handleFinalSubmission
									}
									isReadOnly={true}
								/>
							)}
						</>
					}
					{isServiceApproved || isReviewHostEdits ? (
						<ServiceVettingMerchCopyDiffs
							serviceDiffsMap={serviceDiffsMap}
						/>
					) : (
						<Separator orientation='vertical' />
					)}
					<ServiceVettingMerchOutputForm
						defaultValues={outputFormDefaultValues}
						finalSubmitRef={finalSubmitRef}
						handleFinalSubmissionCb={handleFinalSubmission}
						isHostEdits={isReviewHostEdits || isServiceApproved}
						isLoading={isAiLoading}
						isReadOnly={isFormReadOnly}
					/>
				</div>
			</Container>
		</ScrollArea>
	)
}
ServiceVettingMerchForms.displayName = 'ServiceVettingMerchForms'
